import React from "react"
import {LMTableApi, PaginationModel, SortModel} from "../components/Tables/types";
import PageTitle from "../components/subcomponents/PageTitle";
import {TablePaper} from "../components/TablePaper";
import {Alert, Box, Button, Divider} from "@mui/material";
import TableSearch from "../components/subcomponents/TableSearch";
import AddButton from "../components/subcomponents/Buttons/AddButton";
import DownloadIcon from "@mui/icons-material/Download";
import {ButtonContainer, InnerPaperContainer} from "../components/subcomponents/Containers/Containers";
import CustomerService from "../services/CustomerService";
import {CustomerTableColumns} from "../constants";
import TableFactory from "../components/TableFactory";
import ErrorExclamation from "../components/subcomponents/ErrorExclamation";
import {styled} from "@mui/material/styles";
import {useNavigate} from "react-router-dom";
import DeleteCustomerDialog from "../components/dialogs/DeleteCustomerDialog";
import {useTranslation} from "react-i18next";
import {DataGridCsvExporter} from "../export/csv/DataGridCSVExporter";
import {applySearchFilter, sortBySortModel, timeStamp} from "../utils/helperFunctions";
import {CUSTOMER_NUMBER_SORT_MODEL} from "../utils/customerNumberSort";
import {hasUnknownSalesperson, withSalespersonLabels} from "../utils/customerSalesperson";
import {useUserRole} from "../utils/UserRoleContext";
import {logBusinessAudit} from "../utils/auditLogHelper";

const TableBox = styled(Box)({
  padding: "1.5rem 2rem"
})

export default function CustomerPage() {
  const apiRef = React.useRef<LMTableApi | null>(null);

  const navigate = useNavigate()
  const {isAdmin} = useUserRole();

  const [items, setItems] = React.useState<any[]>([])
  const [error, setError] = React.useState(false)
  const [isLoadingData, setLoadingData] = React.useState(false)
  const [selectedRowIds, setSelectedRowIds] = React.useState<string[]>([]);
  const [searchFilter, setSearchFilter] = React.useState('')

  const [pagination, setPagination] = React.useState<PaginationModel>({page: 0, pageSize: 15});
  const [sortModel, setSortModel] = React.useState<SortModel>(CUSTOMER_NUMBER_SORT_MODEL);

  const {t} = useTranslation()

  React.useEffect(() => {
    loadData().then()

  }, [])

  async function loadData() {
    setLoadingData(true)

    try {
      setItems(await CustomerService.getCustomerTableData())
      setError(false)
    } catch (e) {
      console.error(e)
      setError(true)
    }

    setLoadingData(false)
  }

  function handleSelectionChange(selectionModel: any) {
    if (selectionModel.length > 1) {
      setSelectedRowIds(selectionModel)
    } else {
      setSelectedRowIds(selectionModel.splice(-1))
    }
  }

  function handleAddClick() {
    navigate("/customers/add")
  }

  function handleUpdateClick() {
    if (selectedRowIds.length !== 1) {
      throw new Error(t('pages.customerPage.customerIDParseError'))
    }
    navigate(`/customers/update/${selectedRowIds[0]}?fallbackURL=customers`)
  }

  function isUpdateButtonDisabled() {
    return selectedRowIds.length !== 1
  }

  function isDeleteButtonDisabled() {
    const selectedRowCheck = selectedRowIds.length !== 1;
    return selectedRowCheck || !isAdmin
  }

  // The audit snapshot of a deleted customer holds the customer's own data only, not
  // the salesperson columns the table adds to each row.
  function deletedCustomerSnapshot(id: string) {
    const row = items.find(c => c.id === id)
    if (!row) return null
    const {salesperson, salespersonDisabled, ...customer} = row
    return customer
  }

  async function fullDeleteCustomer() {
    const deletedCustomer = deletedCustomerSnapshot(selectedRowIds[0])
    await CustomerService.deleteCustomer(selectedRowIds[0], false)
    await logBusinessAudit('audit.customer.deleted', selectedRowIds[0], deletedCustomer, null)
  }

  async function safeDeleteCustomer() {
    const deletedCustomer = deletedCustomerSnapshot(selectedRowIds[0])
    await CustomerService.deleteCustomer(selectedRowIds[0], true)
    await logBusinessAudit('audit.customer.deleted', selectedRowIds[0], deletedCustomer, null)
  }

  const displayItems = React.useMemo(() => withSalespersonLabels(items, t), [items, t])

  const filteredItems = React.useMemo(
    () => applySearchFilter(displayItems, searchFilter),
    [displayItems, searchFilter]
  )

  // This page slices the rows itself, so the sort has to be applied to the whole
  // filtered list here — LMTable only ever sees one page and can't order across them.
  const sortedItems = React.useMemo(
    () => sortBySortModel(filteredItems, sortModel, CustomerTableColumns),
    [filteredItems, sortModel]
  )

  const pagedItems = React.useMemo(() => {
    const start = pagination.page * pagination.pageSize
    return sortedItems.slice(start, start + pagination.pageSize)
  }, [sortedItems, pagination])

  function exportCSV() {
    if (!apiRef.current) return;
    const columns = apiRef.current.getVisibleColumns()

    // Deliberately not apiRef.getSortedRows(): this table pages server-side, so the
    // table only holds the rows of the current page and the export would silently be
    // one page of customers. The export covers the whole list the user is looking at,
    // in the order they are looking at it.
    const exporter = new DataGridCsvExporter(sortedItems, columns, {
      filename: `Kunden Tabellen Export ${timeStamp()}`,
      delimiter: ";",
      includeHeaders: true,
    });

    exporter.exportToCsv();
  }

  return (
    <div className={"base-container"}>
      <PageTitle>{t('appBar.customer')}</PageTitle>
      <Divider/>
      <TablePaper>
        <InnerPaperContainer>

          <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                width: '100%',
                gap: 2,
                marginTop: 0,
                paddingTop: 0,
                paddingLeft: 2,
                paddingRight: 2,
                flexFlow: 'row wrap'
              }}>
            <Box sx={{display: 'flex', flexWrap: 'unset', flexDirection: 'column', width: 'calc(50% - 8px)'}}>
              <div style={{display: 'flex', justifyContent: 'left'}}>
                <TableSearch onSearch={(input) => {
                  setSearchFilter(input)
                }}/>
              </div>
            </Box>

            <Box sx={{display: 'flex', flexWrap: 'unset', flexDirection: 'column', width: 'calc(50% - 8px)'}}>
              <div style={{display: 'flex', justifyContent: 'right'}}>
                <AddButton onClick={handleAddClick} disabled={!isAdmin}/>
                <Button
                  variant="contained"
                  disabled={!isAdmin}
                  onClick={exportCSV}
                  startIcon={<DownloadIcon/>}
                >{t('pages.exportCSV')}</Button>
              </div>
            </Box>
          </Box>

        </InnerPaperContainer>
        <TableBox>
          {!error && hasUnknownSalesperson(items) &&
            <Alert severity="warning" sx={{mb: 2}}>{t('pages.customerPage.salespersonLoadIncomplete')}</Alert>}
          {error ?
            <ErrorExclamation message={t('activities.dataFetchFail')}/>
            : TableFactory.createTable(
              "customers",
              CustomerTableColumns.map(elem => elem.field),
              pagedItems,
              selectedRowIds,
              handleSelectionChange,
              (model) => {
                setSortModel(model)
              },
              {sortModel},
              apiRef,
              pagination,
              setPagination,
              filteredItems.length,
              isLoadingData
            )}
        </TableBox>
        <ButtonContainer>
          <DeleteCustomerDialog
            fullDeleteHandler={fullDeleteCustomer}
            safeDeleteHandler={safeDeleteCustomer}
            disabled={isDeleteButtonDisabled()}
            refreshFunction={loadData}
          />
          <Button
            variant="contained"
            disabled={isUpdateButtonDisabled()}
            onClick={handleUpdateClick}
          >
            {isAdmin ? t('activities.edit') : t('activities.view')}
          </Button>
        </ButtonContainer>
      </TablePaper>
    </div>
  )
}
